import {
  Box,
  type BoxProps,
  Typography,
  type TypographyProps,
  styled,
} from "@mui/material"
import type { MouseEvent, PropsWithChildren } from "react"
import { ChevronRight } from "react-feather"
import { Link, type LinkProps } from "react-router-dom"
import type { EmptyObject, MergeExclusive } from "type-fest"
import { isDefined } from "../../../utils"

export type LinkToProps = {
  name: string
  containerSx?: BoxProps["sx"]
  nameSx?: TypographyProps
  chevronSize?: number
  withChevronHoverEffect?: boolean
} & MergeExclusive<ExtendedLinkProps, EmptyObject>

interface ExtendedLinkProps extends LinkProps {
  isExternalLink?: boolean
}

const CHEVRON_CLASSNAME = "link-to-chevron"
const TEXT_CLASSNAME = "link-to-info-text"

export const LinkTo = ({
  name,
  nameSx,
  chevronSize = 15,
  ...props
}: LinkToProps) => {
  return (
    <Container {...props}>
      <InfoText {...nameSx} className={TEXT_CLASSNAME}>
        {name}
        <ChevronRight
          size={chevronSize}
          className={CHEVRON_CLASSNAME}
          style={{ transition: "transform ease-in-out 150ms" }}
        />
      </InfoText>
    </Container>
  )
}

const Container = ({
  to,
  isExternalLink,
  children,
  containerSx,
  withChevronHoverEffect = true,
  ...props
}: PropsWithChildren<
  Partial<ExtendedLinkProps> & {
    containerSx?: BoxProps["sx"]
    withChevronHoverEffect?: boolean
  }
>) => {
  const sx = {
    ...(!!containerSx && containerSx),
    ...(withChevronHoverEffect && chevronHoverEffect),
  }
  return isDefined(to) ? (
    <InfoBox
      component={Link}
      to={to}
      target={isExternalLink ? "_blank" : "_self"}
      onClick={(e: MouseEvent) => e.stopPropagation()}
      sx={sx}
      {...props}
    >
      {children}
    </InfoBox>
  ) : (
    <InfoBox sx={sx}>{children}</InfoBox>
  )
}

export const addLinkToHoverEffect = ({
  opacity = 0.8,
  hoverColor,
}: {
  opacity?: number
  hoverColor?: string
}) => ({
  "&:hover": {
    opacity,
    [`.${CHEVRON_CLASSNAME}`]: {
      transform: "translateX(3px)",
    },
    [`.${TEXT_CLASSNAME}`]: {
      color: hoverColor,
    },
  },
})

export const chevronHoverEffect = addLinkToHoverEffect({})

const InfoBox = styled(Box)(() => ({
  textDecoration: "none",
})) as typeof Box

const InfoText = styled(Typography)(() => ({}))
InfoText.defaultProps = {
  variant: "12px",
  color: "primary.main",
  fontWeight: "500",
  display: "flex",
  alignItems: "center",
  gap: 1,
}
